import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Tooltip,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/system";
import { ReactComponent as ArrowDropUpIcon } from "../icons/ShortIcon.svg";
import { ReactComponent as ArrowDropDownIcon } from "../icons/ShortIconDown.svg";
import { orLossRmTable } from "Samarth Module/api/orltAPI";
import { SelectedAlertsContext } from "utils/SelectedAlertContext";
import Pagination from "Samarth Module/components/Pagination/Pagination";
import MultiSelectWithSearchInput from "Samarth Module/components/dropdown/MultiSelectWithInput";
import "../orLossTreeContainer.css";

const StyledTableCell = styled(TableCell)(() => ({
  fontFamily: "Unilever Shilling",
  fontSize: "10px",
  textAlign: "center",
  whiteSpace: "normal",
  lineHeight: 1.1,
  padding: "4px 6px",
  border: "none",
  minWidth: "70px",
  maxWidth: "90px",
  "&.MuiTableCell-head": {
    fontWeight: 700,
    backgroundColor: "#fff",
    color: "#000",
  },
  "&.MuiTableCell-body": {
    fontWeight: 400,
  },
}));

const StickyColumnHead = styled(StyledTableCell)(() => ({
  position: "sticky",
  backgroundColor: "#fff",
  zIndex: 5,
  fontWeight: 700,
  color: "#354ACD",
  textAlign: "left",
}));

const StickyColumnCell = styled(StyledTableCell)(() => ({
  position: "sticky",
  backgroundColor: "#fff",
  zIndex: 4,
  fontWeight: 700,
  color: "#354ACD",
  textAlign: "left",
}));

const MaterialDescriptionCell = styled(StickyColumnCell)(() => ({
  maxWidth: "250px",
  minWidth: "250px",
  width: "250px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  textAlign: "left",
}));

const StyledTableRow = styled(TableRow)(() => ({
  backgroundColor: "#fff !important",
}));

const getBGColor = (value) => {
  if (!value) {
    return { backgroundColor: "#e1dadac0" };
  }
  // if (value >= 5) return { backgroundColor: "#FCA5A5" };
  // if (value >= 3) return { backgroundColor: "#FFEC9A" };
  return { backgroundColor: "#FCA5A5" };
};

const RMTable = ({tblView}) => {
  const [rmTableData, setRmTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [weekColumns, setWeekColumns] = useState([]);
  const [stickyColumns, setStickyColumns] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [selectedMaterial, setSelectedMaterial] = useState([]);
  const [selectedJIT, setSelectedJIT] = useState([]);
  const { orLossTreeSelectedFilters } = useContext(SelectedAlertsContext);
  const tableContainerRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await orLossRmTable(orLossTreeSelectedFilters, tblView);
        
        if (isMounted && response && response.columns && response.rm_table_data) {
          const sticky = response.columns.slice(0, 4); // First 4 columns: Material ID, Description, UOM, JIT/Non JIT (JIT hidden from display)
          const weeks = response.columns.slice(4);
          setStickyColumns(sticky);
          setWeekColumns(weeks);
          setRmTableData(Array.isArray(response.rm_table_data) ? response.rm_table_data : []);
          setPage(0);
        } else if (isMounted) {
          setStickyColumns([]);
          setWeekColumns([]);
          setRmTableData([]);
        }
        
        if (isMounted) {
          setSortColumn(null);
        }
      } catch (err) {
        console.error("RM table error", err);
        if (isMounted) {
          setRmTableData([]);
          setStickyColumns([]);
          setWeekColumns([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    fetchData();

    return () => {
      isMounted = false;
    };
  }, [JSON.stringify(orLossTreeSelectedFilters), tblView]);

  useEffect(() => {
    if (tableContainerRef.current && rmTableData.length > 0 && !loading) {
      tableContainerRef.current.scrollTo({
        top: 0,
        left: tableContainerRef.current.scrollWidth,
        behavior: "smooth"
      });
    }
  }, [rmTableData, loading]);

  const handleSort = (column) => {
    const order = sortColumn === column && sortOrder === "asc" ? "desc" : "asc";
    setSortColumn(column);
    setSortOrder(order);

    const sorted = [...rmTableData].sort((a, b) => {
      const valA = a[column]?.ShortFall ?? 0;
      const valB = b[column]?.ShortFall ?? 0;
      return order === "asc" ? valA - valB : valB - valA;
    });

    setRmTableData(sorted);
  };

  const chartHeight = () => {
    const mediaQuery1 = window.matchMedia("(max-width: 1300px)").matches;
    const mediaQuery2 = window.matchMedia("(max-width: 1450px)").matches;
    const mediaQuery3 = window.matchMedia("(max-width: 1550px)").matches;
    return mediaQuery1 ? 216 : mediaQuery2 ? 240 : mediaQuery3 ? 320 : 300;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const filteredTableData = useMemo(() => {
    let filteredData = rmTableData;
    
    // Filter by selected material if any are selected
    if (selectedMaterial.length > 0 && stickyColumns.length > 0) {
      filteredData = filteredData.filter(row => selectedMaterial.includes(row[stickyColumns[0]]));
    }
    
    // Filter by JIT/Non-JIT if any are selected (index 3)
    if (selectedJIT.length > 0 && stickyColumns.length > 3) {
      filteredData = filteredData.filter(row => {
        const jitValue = row[stickyColumns[3]];
        const displayValue = (jitValue && jitValue.trim() !== '') ? jitValue : '';
        return selectedJIT.includes(displayValue);
      });
    }
    
    return filteredData;
  }, [rmTableData, selectedMaterial, selectedJIT, stickyColumns]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredTableData.slice(startIndex, endIndex);
  }, [filteredTableData, page, rowsPerPage]);

  const materialOptions = useMemo(() => {
    if (!Array.isArray(rmTableData) || rmTableData.length === 0 || stickyColumns.length < 2) return [];
    return rmTableData.map(row => `${row[stickyColumns[0]]} : ${row[stickyColumns[1]]}`).filter(Boolean);
  }, [rmTableData, stickyColumns]);

  const jitOptions = useMemo(() => {
    if (!Array.isArray(rmTableData) || rmTableData.length === 0 || stickyColumns.length < 4) return [];
    const options = Array.from(new Set(rmTableData.map(row => {
      const val = row[stickyColumns[3]];
      return (val && val.trim() !== '') ? val : '';
    })));
    return options;
  }, [rmTableData, stickyColumns]);

  const handleMaterialFilter = (label, values) => {
    // Extract Material IDs from "ID : Description" format
    const materialIds = values.map(val => val.split(' : ')[0]);
    setSelectedMaterial(materialIds);
    setPage(0);
  };

  const handleJITFilter = (label, values) => {
    setSelectedJIT(values);
    setPage(0);
  };

  const isNoData = useMemo(() => {
    return !Array.isArray(rmTableData) || rmTableData.length === 0;
  }, [rmTableData]);

  return (
    <Box sx={{
      p: 0,
      borderRadius: 2,
      width: "100%",
      backgroundColor: "#fff",
      display: "flex",
      flexDirection: "column",
      height: "49vh"
    }}>
      {/* Header */}
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexShrink: 0,
        height: "30px",
        p: 1,
        pt: 0
      }}>
        <Typography sx={{ fontWeight: 700, fontSize: "13px" }}>
          RM Table
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Box sx={{ minWidth: "200px", maxWidth: "200px", width: "200px" }}>
            <MultiSelectWithSearchInput
              label="Material ID"
              options={materialOptions}
              filterValues={materialOptions.filter(opt => selectedMaterial.includes(opt.split(' : ')[0]))}
              handleFilter={handleMaterialFilter}
              disabled={false}
              isLabelShow={true}
              type="RM Table"
              isMultiSelect={true}
            />
          </Box>
          <Box sx={{ minWidth: "150px", maxWidth: "150px", width: "150px" }}>
            <MultiSelectWithSearchInput
              label="JIT/Non JIT"
              options={jitOptions}
              filterValues={selectedJIT}
              handleFilter={handleJITFilter}
              disabled={false}
              isLabelShow={true}
              type="RM Table"
              isMultiSelect={true}
            />
          </Box>
        </Box>
      </Box>

      {/* Divider */}
      <Box sx={{
        borderBottom: "1px solid #EAECF0",
        flexShrink: 0,
        marginTop: '10px'
      }} />

      {/* Table container */}
      <Box sx={{
        flex: 1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column"
      }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : isNoData ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              fontSize: "14px"
            }}
          >
            Data is not available
          </Box>
        ) : (
          <Box
            ref={tableContainerRef}
            sx={{ 
              maxWidth: "100%", 
              maxHeight: chartHeight(), 
              overflow: "auto",
              "&::-webkit-scrollbar": {
                width: "6px",
                height: "6px",
              },
            }}
          >
            <Table size='small' stickyHeader sx={{ minWidth: "100%" }}>
                <TableHead>
                  <TableRow>
                    {stickyColumns.slice(0, 3).map((col, idx) => {
                      let leftPosition = 0;
                      if (idx === 1) leftPosition = "70px";  // Material Description
                      if (idx === 2) leftPosition = "320px"; // UOM (70px + 250px)
                      return (
                        <StickyColumnHead key={idx} sx={{ left: leftPosition }}>
                          {col}
                        </StickyColumnHead>
                      );
                    })}
                    {weekColumns.map((week, idx) => (
                      <StyledTableCell key={idx}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.2,
                          }}
                        >
                          <Box>{week}</Box>
                          <IconButton
                            size='small'
                            onClick={() => handleSort(week)}
                            sx={{ padding: 0, marginLeft: "2px", color: "#000" }}
                          >
                            {sortColumn === week ? (
                              sortOrder === "asc" ? (
                                <ArrowDropUpIcon fontSize='small' />
                              ) : (
                                <ArrowDropDownIcon fontSize='small' />
                              )
                            ) : (
                              <ArrowDropUpIcon
                                fontSize='small'
                                sx={{ opacity: 0.3 }}
                              />
                            )}
                          </IconButton>
                        </Box>
                      </StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((row, idx) => (
                    <StyledTableRow key={idx}>
                      {stickyColumns.slice(0, 3).map((col, colIdx) => {
                        const cellValue = row[col] ?? "N/A";
                        const isMaterialDescription = col.toLowerCase().includes("description");
                        const CellComponent = isMaterialDescription ? MaterialDescriptionCell : StickyColumnCell;
                        
                        // Calculate left position for each column
                        let leftPosition = 0;
                        if (colIdx === 1) leftPosition = "70px";  // Material Description
                        if (colIdx === 2) leftPosition = "320px"; // UOM (70px + 250px)
                        
                        return (
                          <CellComponent key={colIdx} sx={{ left: leftPosition }}>
                            {/* {isMaterialDescription ? (
                              <Tooltip 
                                title={<span style={{ color: '#2b2b2b' }}>{cellValue}</span>} 
                                placement="top"
                              >
                                <span>{cellValue}</span>
                              </Tooltip>
                            ) : (
                            )} */}
                            {cellValue}
                          </CellComponent>
                        );
                      })}
                      {weekColumns.map((week, colIdx) => {
                        const weekData = row[week];
                        const shortFall = weekData?.ShortFall ?? 0;
                        const demand = weekData?.ShortFall ? weekData?.Demand ?? 0 : 0;
                        const supply = weekData?.ShortFall ?  weekData?.Supply ?? 0 : 0;
                        
                        const tooltipContent = weekData ? (
                          <div className="customTooltip">
                            <p><span className='label'>Demand :</span> {demand.toFixed(2)}</p>
                            <p><span className='label'>Supply :</span> {supply.toFixed(2)}</p>
                            <p><span className='label'>Shortfall :</span> {shortFall.toFixed(2)}</p>
                          </div>
                        ) : "No data";

                        return (
                          <StyledTableCell key={colIdx}>
                            <Tooltip title={tooltipContent} placement='top'>
                              <div
                                style={{
                                  ...getBGColor(shortFall),
                                  borderRadius: "3px",
                                  padding: "4px 6px",
                                  fontSize: "10px",
                                  color: !shortFall || shortFall === 0 ? "#e1dadac0" : "#000",
                                }}
                              >
                                {shortFall ? shortFall.toFixed(2) : "0.0"}
                              </div>
                            </Tooltip>
                          </StyledTableCell>
                        );
                      })}
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
        )}
      </Box>

      {/* Pagination */}
      {!isNoData && (
        <Box sx={{ borderTop: "1px solid #EAECF0", flexShrink: 0 }}>
          <Pagination
            page={page}
            tableData={filteredTableData}
            dataPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </Box>
      )}

      {/* Footer - Legend */}
      {/* {!isNoData && (
        <Box sx={{
          borderTop: "1px solid #EAECF0",
          pt: 0.5,
          pb: 0,
          height: "28px",
          flexShrink: 0
        }}>
          <Box sx={{ display: "flex", gap: 1.5, pl: 1 }}>
            <LegendItem label='Greater than 0' color="#FCA5A5" />
          </Box>
        </Box>
      )} */}
    </Box>
  );
};

const LegendItem = ({ label, color }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "4px", pt: "4px" }}>
    <Box
      component='span'
      sx={{
        width: "2px",
        height: "12px",
        backgroundColor: color,
        display: "inline-block",
        borderRadius: "1px",
      }}
    />
    <Typography sx={{ fontSize: "11px", color: "#000" }}>{label}</Typography>
  </Box>
);

export default RMTable;